<template>
  <div>
    <h2 class="mc-h2">{{ title }}</h2>
    <p class="mc-lede">
      Search for a road, estate or landmark, drag the pin to fine-tune, or use
      your current location.
    </p>

    <div class="mc-field">
      <label class="mc-sr" for="mc-locsearch">Search for a location</label>
      <input
        id="mc-locsearch"
        ref="searchInput"
        type="text"
        placeholder="Search for a location"
        v-model="query"
      />
    </div>

    <button
      type="button"
      class="mc-btn mc-btn--sec mc-wide mc-mb12"
      :disabled="locating"
      @click="useCurrentLocation"
    >
      {{ locating ? 'Getting your location…' : '📍 Use my current location' }}
    </button>

    <div ref="mapEl" class="mc-map" />

    <p v-if="draft.locationName" class="mc-hint mc-loc-confirm">
      📍 {{ draft.locationName }}
    </p>

    <button
      type="button"
      class="mc-btn mc-btn--pri mc-wide mc-mt16"
      :disabled="!draft.locationName && query.trim().length < 3"
      @click="confirm"
    >
      Use this location
    </button>
    <button
      type="button"
      class="mc-btn mc-btn--ghost mc-wide mc-mt8"
      @click="$emit('cancel')"
    >
      Cancel
    </button>
  </div>
</template>

<script>
import { Loader } from '@googlemaps/js-api-loader'
import { getGeolocation } from '@/utils/deviceRef'

const NAIROBI = { lat: -1.286389, lng: 36.817223 }

// One loader/script tag for the whole app session, regardless of how many
// times this component mounts — but only once it actually succeeds. A failed
// load (network blip, ad blocker) must not be cached, or a later mount could
// never retry for the rest of the session.
let loaderPromise = null
function loadGoogleMaps() {
  if (!loaderPromise) {
    loaderPromise = new Loader({
      apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
      libraries: ['places'],
    })
      .load()
      .catch((e) => {
        loaderPromise = null
        throw e
      })
  }
  return loaderPromise
}

export default {
  name: 'LocationPicker',
  props: {
    // { latitude, longitude, locationName }
    value: { type: Object, default: () => ({}) },
    title: { type: String, default: 'Where did it happen?' },
  },
  data() {
    return {
      query: this.value.locationName || '',
      draft: {
        latitude: this.value.latitude || null,
        longitude: this.value.longitude || null,
        locationName: this.value.locationName || '',
      },
      locating: false,
      map: null,
      marker: null,
      geocoder: null,
    }
  },
  watch: {
    // Typing something new after a suggestion/pin was already confirmed
    // invalidates the coordinates until the next selection or drag.
    query(newVal) {
      if (newVal !== this.draft.locationName) {
        this.draft.latitude = null
        this.draft.longitude = null
      }
    },
  },
  async mounted() {
    const google = await loadGoogleMaps()
    this.initMap(google)
    this.initAutocomplete(google)
  },
  beforeDestroy() {
    // Google appends .pac-container to body and never removes it; left behind
    // it paints a stray grey line over the form.
    document.querySelectorAll('.pac-container').forEach((el) => el.remove())
  },
  methods: {
    initMap(google) {
      const center =
        this.draft.latitude && this.draft.longitude
          ? { lat: this.draft.latitude, lng: this.draft.longitude }
          : NAIROBI
      this.map = new google.maps.Map(this.$refs.mapEl, {
        center,
        zoom: this.draft.latitude ? 16 : 12,
        streetViewControl: false,
        mapTypeControl: false,
      })
      this.marker = new google.maps.Marker({
        position: center,
        map: this.map,
        draggable: true,
      })
      this.geocoder = new google.maps.Geocoder()
      // Tap-to-place. Before, only a drag moved the pin, so the coordinates
      // and the name never updated and confirming stayed disabled.
      this.map.addListener('click', (event) => {
        this.marker.setPosition(event.latLng)
        this.dropPin(event.latLng.lat(), event.latLng.lng())
      })
      this.marker.addListener('dragend', () => {
        const pos = this.marker.getPosition()
        this.dropPin(pos.lat(), pos.lng())
      })
    },
    // Pin, coordinates and name always move together.
    dropPin(lat, lng) {
      this.draft.latitude = lat
      this.draft.longitude = lng
      if (!this.geocoder) return
      this.geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        this.draft.locationName =
          status === 'OK' && results[0]
            ? results[0].formatted_address
            : `Lat ${lat.toFixed(5)}, Lng ${lng.toFixed(5)}`
        this.query = this.draft.locationName
      })
    },
    initAutocomplete(google) {
      const autocomplete = new google.maps.places.Autocomplete(
        this.$refs.searchInput,
        {
          componentRestrictions: { country: 'ke' },
          fields: ['geometry', 'formatted_address', 'name'],
        }
      )
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        if (!place.geometry) return
        const loc = place.geometry.location
        this.draft.latitude = loc.lat()
        this.draft.longitude = loc.lng()
        this.draft.locationName = place.formatted_address || place.name
        this.query = this.draft.locationName
        this.recenter(loc.lat(), loc.lng())
      })
    },
    recenter(lat, lng) {
      const pos = { lat, lng }
      if (this.map) {
        this.map.setCenter(pos)
        this.map.setZoom(16)
      }
      if (this.marker) this.marker.setPosition(pos)
    },
    async useCurrentLocation() {
      this.locating = true
      const pos = await getGeolocation()
      this.locating = false
      if (!pos) {
        this.$emit(
          'error',
          "Couldn't get your location — search for it instead."
        )
        return
      }
      this.recenter(pos.latitude, pos.longitude)
      this.dropPin(pos.latitude, pos.longitude)
    },
    confirm() {
      // Typed text with no autocomplete/pin selection — keep the name, no
      // coordinates. IncidentLocation.hasCoordinates() handles this on the
      // backend, so it's a safe, deliberate fallback rather than a dead end.
      if (!this.draft.locationName && this.query.trim().length >= 3) {
        this.draft.locationName = this.query.trim()
      }
      this.$emit('select', { ...this.draft })
    },
  },
}
</script>

<style scoped>
.mc-h2 {
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #14142b;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 20px;
}

.mc-field {
  margin: 0 0 16px;
}

.mc-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

input {
  width: 100%;
  font-family: inherit;
  font-size: 16px;
  color: #333333;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 13px 14px;
  appearance: none;
}

input:focus {
  outline: 0;
  border-color: #0066cc;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.13);
}

.mc-map {
  height: 220px;
  border-radius: 14px;
  margin-bottom: 12px;
  background: #eff0f6;
}

.mc-hint {
  font-size: 12.5px;
  color: #6e7191;
  margin: 6px 0 0;
}

.mc-loc-confirm {
  font-weight: 600;
  color: #002247;
}

.mc-wide {
  width: 100%;
}

.mc-mb12 {
  margin-bottom: 12px;
}

.mc-mt8 {
  margin-top: 8px;
}

.mc-mt16 {
  margin-top: 16px;
}
</style>
